import {useCallback, useEffect, useRef, useState} from 'react'

/**
 * Single-owner guard for the PolicyDial webphone.
 *
 * Agents routinely keep several PolicyTools tabs open. If more than one tab mounts
 * the webphone iframe, they each register the same SIP extension — the later
 * registration steals the contact and the first tab silently goes dead, or both
 * join the MeetMe and the audio echoes/splits. The agent then can't tell which tab
 * "has" the call. This hook elects exactly ONE owner tab; the others render a
 * "running in another tab" panel instead of a second webphone.
 *
 * Coordination is cross-tab and frontend-only (no backend/vici change):
 *  - localStorage holds the current owner `{ tabId, ts, inCall }` (the source of truth).
 *  - A heartbeat refreshes `ts`; an entry older than STALE_MS is considered abandoned
 *    (covers a crashed/closed owner). STALE_MS is comfortably longer than a reload, and
 *    the tabId lives in sessionStorage so it survives F5 — a refresh keeps ownership.
 *  - BroadcastChannel makes claims/releases/takeovers instant instead of waiting for
 *    the next heartbeat tick.
 */

const OWNER_KEY = 'policytools_webphone_owner'
const TAB_KEY = 'policytools_webphone_tab'
const CHANNEL = 'policytools_webphone'
const HEARTBEAT_MS = 1500
const STALE_MS = 5000

interface OwnerRecord {
  tabId: string
  ts: number
  inCall: boolean
}

interface UseWebphoneOwnershipResult {
  /** This tab owns the webphone (mount the iframe). */
  isOwner: boolean
  /** Another live tab currently owns the webphone. */
  ownerElsewhere: boolean
  /** The owning tab is on a live call (block takeover). */
  ownerInCall: boolean
  /** Ask to move the webphone to this tab. No-op if the owner is on a call. */
  requestTakeover: () => void
}

const getTabId = (): string => {
  let id = ''
  try {
    id = sessionStorage.getItem(TAB_KEY) || ''
    if (!id) {
      id = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
      sessionStorage.setItem(TAB_KEY, id)
    }
  } catch {
    id = `${Date.now().toString(36)}-mem`
  }
  return id
}

const readOwner = (): OwnerRecord | null => {
  try {
    const raw = localStorage.getItem(OWNER_KEY)
    if (!raw) return null
    const rec = JSON.parse(raw) as OwnerRecord
    if (!rec || !rec.tabId) return null
    if (Date.now() - rec.ts > STALE_MS) return null // abandoned
    return rec
  } catch {
    return null
  }
}

const useWebphoneOwnership = ({
  active,
  inCall,
}: {
  active: boolean
  inCall: boolean
}): UseWebphoneOwnershipResult => {
  const [isOwner, setIsOwner] = useState(false)
  const [ownerElsewhere, setOwnerElsewhere] = useState(false)
  const [ownerInCall, setOwnerInCall] = useState(false)

  const tabIdRef = useRef<string>('')
  const channelRef = useRef<BroadcastChannel | null>(null)
  const inCallRef = useRef(inCall)
  const activeRef = useRef(active)
  inCallRef.current = inCall
  activeRef.current = active

  if (!tabIdRef.current) tabIdRef.current = getTabId()

  const writeOwner = useCallback((withCall: boolean) => {
    try {
      localStorage.setItem(
        OWNER_KEY,
        JSON.stringify({tabId: tabIdRef.current, ts: Date.now(), inCall: withCall})
      )
    } catch {
      /* ignore */
    }
  }, [])

  const releaseOwner = useCallback(() => {
    const owner = readOwner()
    if (owner && owner.tabId === tabIdRef.current) {
      try {
        localStorage.removeItem(OWNER_KEY)
      } catch {
        /* ignore */
      }
      channelRef.current?.postMessage({type: 'released', from: tabIdRef.current})
    }
  }, [])

  // Core evaluation: decide whether this tab is/should be the owner.
  const evaluate = useCallback(() => {
    if (!activeRef.current) {
      // This tab no longer wants the webphone — hand off if we held it.
      releaseOwner()
      setIsOwner(false)
      setOwnerElsewhere(false)
      setOwnerInCall(false)
      return
    }
    const owner = readOwner()
    if (!owner) {
      // No live owner — claim it.
      writeOwner(inCallRef.current)
      channelRef.current?.postMessage({type: 'claimed', from: tabIdRef.current})
      setIsOwner(true)
      setOwnerElsewhere(false)
      setOwnerInCall(false)
      // Resolve simultaneous-claim races: if two tabs claimed in the same tick,
      // localStorage keeps the last writer — re-read and yield if we lost.
      window.setTimeout(() => {
        const confirmed = readOwner()
        if (confirmed && confirmed.tabId !== tabIdRef.current) {
          setIsOwner(false)
          setOwnerElsewhere(true)
          setOwnerInCall(Boolean(confirmed.inCall))
        }
      }, 90)
      return
    }
    if (owner.tabId === tabIdRef.current) {
      // We are the owner — refresh heartbeat with current call state.
      writeOwner(inCallRef.current)
      setIsOwner(true)
      setOwnerElsewhere(false)
      setOwnerInCall(false)
      return
    }
    // Someone else owns it.
    setIsOwner(false)
    setOwnerElsewhere(true)
    setOwnerInCall(Boolean(owner.inCall))
  }, [releaseOwner, writeOwner])

  const requestTakeover = useCallback(() => {
    const owner = readOwner()
    if (owner && owner.tabId !== tabIdRef.current && owner.inCall) {
      // Owner is on a live call — refuse (the UI also disables the button).
      return
    }
    // Ask the current owner to step down, then claim.
    channelRef.current?.postMessage({type: 'takeover', from: tabIdRef.current})
    setTimeout(evaluate, 120)
  }, [evaluate])

  useEffect(() => {
    let channel: BroadcastChannel | null = null
    try {
      channel = new BroadcastChannel(CHANNEL)
      channelRef.current = channel
    } catch {
      channelRef.current = null
    }

    const onMessage = (event: MessageEvent) => {
      const msg = event.data as {type?: string; from?: string} | undefined
      if (!msg) return
      if (msg.type === 'takeover') {
        // Another tab wants ownership: yield only if we're owner and not on a call.
        const owner = readOwner()
        if (owner && owner.tabId === tabIdRef.current && !inCallRef.current) {
          releaseOwner()
          setIsOwner(false)
        }
        return
      }
      if (msg.type === 'released' || msg.type === 'claimed') {
        evaluate()
      }
    }
    if (channel) channel.onmessage = onMessage

    // localStorage events as a fallback signal across tabs.
    const onStorage = (e: StorageEvent) => {
      if (e.key === OWNER_KEY) evaluate()
    }
    window.addEventListener('storage', onStorage)

    evaluate()
    const hb = window.setInterval(evaluate, HEARTBEAT_MS)

    // On reload we deliberately do NOT release (staleness covers a real close while
    // the sessionStorage tabId lets a refresh reclaim its own ownership).
    return () => {
      window.clearInterval(hb)
      window.removeEventListener('storage', onStorage)
      if (channel) {
        channel.onmessage = null
        channel.close()
      }
      channelRef.current = null
    }
  }, [evaluate, releaseOwner])

  // React to active/inCall changes immediately.
  useEffect(() => {
    evaluate()
  }, [active, inCall, evaluate])

  return {isOwner, ownerElsewhere, ownerInCall, requestTakeover}
}

export default useWebphoneOwnership
